import { ChangeDetectionStrategy, Component, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, ControlValueAccessor, NgControl } from '@angular/forms';
import { DownlineTreeviewItem, OrderDownlineTreeviewEventParser, TreeviewConfig, TreeviewEventParser, TreeviewItem, TreeviewItemTemplateContext } from '@laijuthomas/ngx-treeview';
import { delay, of } from 'rxjs';
import { getRandomId } from 'src/app/shared/extensions/string.extensions';
import { ISelectedTreeviewItemTooltip } from './models/selected-treeview-item-tooltip.interface';
import { ITreeViewItemValue } from './models/treeview-item-value.interface';
import { ITreeviewItem } from './models/treeview-item.interface';
import { ISelectedTreeviewItem } from './selected-treeview-item.interface';

@Component({
    selector: 'app-standard-treeview',
    templateUrl: './standard-treeview.component.html',
    styleUrls: ['./standard-treeview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{ provide: TreeviewEventParser, useClass: OrderDownlineTreeviewEventParser }],
    standalone: false,
})
export class StandardTreeviewComponent implements ControlValueAccessor, OnInit, OnChanges {
    private controlContainer = inject(ControlContainer, { optional: true });
    private detroyRef = inject(DestroyRef);
    public ngControl = inject(NgControl, { self: true, optional: true });

    @Input() public label: string;
    @Input() public hint: string;
    @Input() public infoText: string;
    @Input() public disabled = false;
    @Input() public hasAllCheckBox: boolean;
    @Input() public hasFilter: boolean;
    @Input() public hasCollapseExpand: boolean;
    @Input() public decoupleChildFromParent: boolean;
    @Input() public itemSeperator = '|';
    @Input() public maxHeight = 200;
    @Input() public loading: boolean;
    @Input() public items: TreeviewItem[];
    @Input() public selectionLimit = 10;
    @Input() public itemTemplate: TemplateRef<TreeviewItemTemplateContext>;
    @Input() public singleSelection = false;

    public values: ITreeViewItemValue[];
    public id: string;
    public config: TreeviewConfig;
    public selectedItemsToShow: ISelectedTreeviewItem[];

    constructor() {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }

        this.id = getRandomId();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const loadingChange = changes.loading;

        if (loadingChange?.previousValue != loadingChange?.currentValue && this.items) {
            this.updateItems(this.items, this.values);
        }
    }

    public ngOnInit(): void {
        this.config = TreeviewConfig.create({
            hasAllCheckBox: this.hasAllCheckBox,
            hasFilter: this.hasFilter,
            hasCollapseExpand: this.hasCollapseExpand,
            decoupleChildFromParent: this.decoupleChildFromParent,
            maxHeight: this.maxHeight,
        });
    }

    public onChangeFn = (_: object[]) => {
        return;
    };

    public onTouchedFn = () => {
        return;
    };

    public registerOnChange(fn: () => void): void {
        this.onChangeFn = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    public writeValue(value: ITreeViewItemValue[]): void {
        if (value != null) {
            this.values = value;
        } else {
            this.values = [];
        }

        this.updateItems(this.items, this.values);

        of(Boolean)
            .pipe(delay(1), takeUntilDestroyed(this.detroyRef))

            .subscribe(() => {
                this.controlContainer.control?.markAsPristine();
            });
    }

    public onSelectionChange(downlineItems: DownlineTreeviewItem[]) {
        const selectedItemsToShow: ISelectedTreeviewItem[] = [];
        for (const downlineItem of downlineItems) {
            const item = downlineItem.item;
            const value = item.value;
            const texts = [item.text];
            let parent = downlineItem.parent;

            while (parent) {
                texts.push(parent.item.text);
                parent = parent.parent;
            }

            const reverseTexts = texts.reverse();
            const displayText = reverseTexts.join(` ${this.itemSeperator} `);

            if (selectedItemsToShow.length >= this.selectionLimit) {
                downlineItem.item.checked = false;
                continue;
            }

            selectedItemsToShow.push({ text: displayText, value, tooltip: { parentItemText: reverseTexts[0], childItemText: reverseTexts[1] } as ISelectedTreeviewItemTooltip } as ISelectedTreeviewItem);
        }
        this.selectedItemsToShow = this.getSelectedItems(selectedItemsToShow);
        this.values = this.getValues();
        this.onChangeFn(this.values);
    }

    private getSelectedItems(selectedItemsToShow: ISelectedTreeviewItem[]) {
        if (this.singleSelection) {
            return this.getSingleSelectedItem(selectedItemsToShow);
        }

        this.selectedItemsToShow = [];

        for (const value of this.values) {
            const existingIndex = selectedItemsToShow.findIndex((item) => item.value.id === value.id);
            if (existingIndex === -1) {
                continue;
            }

            this.selectedItemsToShow.push(selectedItemsToShow[existingIndex]);
            selectedItemsToShow.splice(existingIndex, 1);
        }

        return [...this.selectedItemsToShow, ...selectedItemsToShow];
    }

    private getSingleSelectedItem(selectedItemsToShow: ISelectedTreeviewItem[]) {
        const newSelectedItem = selectedItemsToShow.find((item) => !this.values?.some((prevItem) => prevItem.id == item.value.id));

        if (newSelectedItem) {
            this.items?.forEach((item) => this.updateCheckboxState(item, newSelectedItem.value.id));
            this.selectedItemsToShow = [newSelectedItem];

            return this.selectedItemsToShow;
        }

        this.selectedItemsToShow = selectedItemsToShow?.length ? selectedItemsToShow : [];
        return this.selectedItemsToShow;
    }

    public removeItem(item: ISelectedTreeviewItem, event: Event) {
        event.preventDefault();
        event.stopPropagation();

        this.selectedItemsToShow = this.selectedItemsToShow.filter((i) => i.value.id != item.value.id);
        this.values = this.getValues();

        this.updateItems(this.items, this.values);
        this.onChangeFn(this.values);
    }

    public clearSelection(event: Event) {
        event.preventDefault();
        event.stopPropagation();

        this.selectedItemsToShow = [];
        this.values = this.getValues();

        this.updateItems(this.items, this.values);
        this.onChangeFn(this.values);
    }

    private updateCheckboxState(item: TreeviewItem, selectedId: string) {
        if (!item) {
            return;
        }

        item.checked = item.value.id == selectedId;
        item.children?.forEach((child) => this.updateCheckboxState(child, selectedId));
    }

    private getValues() {
        return this.selectedItemsToShow.map((m) => {
            return {
                ...m.value,
                name: m.text,
            };
        });
    }

    private updateItems(items: TreeviewItem[], selected: ITreeViewItemValue[]) {
        this.items = items?.map((item) => this.mapItem(item, selected));
    }

    private mapItem(item: TreeviewItem, selected: ITreeViewItemValue[]): TreeviewItem {
        if (item.children && item.children.length > 0) {
            return this.updateTreeParent(item, selected);
        }

        return this.updateTreeLeaf(item, selected);
    }

    private updateTreeParent = (item: TreeviewItem, selected: ITreeViewItemValue[]) => {
        this.syncWithExistingSelection(selected, item);

        const children = item.children.map((child) => this.mapItem(child, selected));

        if (JSON.stringify(children) == JSON.stringify(item.children)) {
            return item;
        }

        const modifiedParent = {
            ...item,
            children,
        };

        return new TreeviewItem(modifiedParent, true);
    };

    private updateTreeLeaf = (item: ITreeviewItem, selected: ITreeViewItemValue[]) => {
        this.syncWithExistingSelection(selected, item);

        const shouldBeChecked = selected.some((i) => i.id == item.value.id);

        if (item.checked === shouldBeChecked) {
            return item;
        }

        const modifiedItem = {
            ...item,
            checked: shouldBeChecked,
        };

        return new TreeviewItem(modifiedItem);
    };

    private syncWithExistingSelection(selected: ITreeViewItemValue[], item: ITreeviewItem) {
        const existingSelectedItem = selected.find((s) => s.id == item?.value?.id);
        if (existingSelectedItem) {
            item.value = { ...item.value, ...existingSelectedItem };
        }
    }
}
